import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { versionConfig } from '@/config/versions';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { AuthIntro } from '@/components/auth/AuthIntro';
import { AuthCard } from '@/components/auth/AuthCard';
import { AuthField } from '@/components/auth/AuthField';
import { AuthAlert } from '@/components/auth/AuthAlert';
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton';

type LoginFormData = {
  email: string;
  password: string;
};

const NewLoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const schema = yup.object({
    email: yup
      .string()
      .email(t('app.auth.please_enter_valid_email'))
      .required(t('app.auth.email_required')),
    password: yup
      .string()
      .min(6, t('app.auth.password_min_6'))
      .required(t('app.auth.password_required')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      setError(null);
      await login({
        email: data.email.trim(),
        password: data.password,
        rememberMe: false,
      });

      // Trigger profile refetch after login
      // This is needed because useAuth creates local state per component
      const { triggerProfileRefetch } = await import('@/contexts/UserProfileContext');
      await triggerProfileRefetch();

      navigate(versionConfig.getDefaultRoute());
    } catch (err: unknown) {
      let errorMessage: string;

      // Verificar se é um erro 401 (credenciais inválidas)
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number } };
        if (axiosError.response?.status === 401) {
          errorMessage = t('app.auth.errors.invalid_credentials');
        } else {
          errorMessage = err instanceof Error ? err.message : t('app.auth.errors.login_error');
        }
      } else {
        errorMessage = err instanceof Error ? err.message : t('app.auth.errors.login_error');
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthIntro
        badge={t('app.auth.signin_badge')}
        title={t('app.auth.signin_title')}
        description={t('app.auth.signin_subtitle')}
      />

      <AuthCard>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
          {error ? <AuthAlert>{error}</AuthAlert> : null}

          <AuthField
            {...register('email')}
            label={t('app.auth.email')}
            type="email"
            autoComplete="email"
            placeholder={t('app.auth.email_placeholder')}
            error={errors.email?.message}
          />

          <AuthField
            {...register('password')}
            label={t('app.auth.password')}
            revealable
            autoComplete="current-password"
            placeholder={t('app.auth.enter_password_placeholder')}
            error={errors.password?.message}
            labelAction={
              <RouterLink
                to="/forgot-password"
                className="text-xs font-semibold text-auth-accent hover:underline"
              >
                {t('app.auth.forgot_short')}
              </RouterLink>
            }
          />

          <AuthSubmitButton loading={loading} loadingLabel={t('app.auth.signing_in')}>
            {t('app.auth.sign_in')}
          </AuthSubmitButton>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default NewLoginPage;
